interface Chapter {
  title: string;
  content: string[];
  type: 'introduction' | 'chapter' | 'conclusion';
  chapter_number?: number;
}

interface ParsedOutline {
  title: string;
  chapters: Chapter[];
}

/** Strip markdown bold/italic markers from text */
function stripMarkdown(text: string): string {
  return text.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1').replace(/^\*+\s*/, '').replace(/\s*\*+$/, '').trim();
}

function toTitleCase(text: string): string {
  const minorWords = ['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with'];
  
  return text
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      if (index === 0 || !minorWords.includes(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(' ');
}

export function parseBookOutline(text: string): ParsedOutline {
  // Input validation
  if (!text || typeof text !== 'string') {
    console.warn('Invalid input to parseBookOutline');
    return { title: '', chapters: [] };
  }

  // Split into lines, preserving indentation info for nesting detection
  const rawLines = text.split('\n');
  const lines: string[] = [];
  const indentLevels: number[] = [];

  for (const raw of rawLines) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) continue;
    // Measure indent: count leading spaces (treat tab as 2 spaces)
    const indent = raw.search(/\S/);
    lines.push(trimmed);
    indentLevels.push(indent >= 0 ? indent : 0);
  }

  if (lines.length === 0) {
    console.warn('No content to parse');
    return { title: '', chapters: [] };
  }

  // Check if the first line is a chapter/intro/conclusion heading rather than a book title
  const firstClean = lines[0].replace(/^[#*-]+\s*/, '').trim();
  const firstLower = firstClean.toLowerCase();
  const firstLineIsChapter =
    firstLower.startsWith('introduction') || firstLower.includes('introduction:') ||
    firstLower.startsWith('conclusion') || firstLower.includes('conclusion:') ||
    firstLower.includes('chapter') || /^\d+[:.)-]\s/.test(firstClean);

  // If the first line looks like a chapter heading, don't consume it as the title
  const title = firstLineIsChapter ? '' : toTitleCase(lines[0].replace(/^#+\s*/, '').trim());
  const startIndex = firstLineIsChapter ? 0 : 1;
  const chapters: Chapter[] = [];
  let currentChapter: Chapter | null = null;
  let currentContent: string[] = [];

  // Helper function to save current chapter
  const saveCurrentChapter = () => {
    if (currentChapter) {
      chapters.push({
        ...currentChapter,
        content: [...currentContent]
      });
    }
  };

  // Helper function to extract chapter number
  const extractChapterNumber = (line: string): { number: number | undefined; title: string } => {
    // Try to match "Chapter X:" or "Chapter X -" pattern
    const chapterMatch = line.match(/^chapter\s*(\d+)(?:[:.-]\s*|\s+)(.+)/i);
    if (chapterMatch) {
      return {
        number: parseInt(chapterMatch[1], 10),
        title: stripMarkdown(chapterMatch[2].trim())
      };
    }

    // Try to match just a number at the start
    const numberMatch = line.match(/^(\d+)(?:[:.-]\s*|\s+)(.+)/i);
    if (numberMatch) {
      return {
        number: parseInt(numberMatch[1], 10),
        title: stripMarkdown(numberMatch[2].trim())
      };
    }

    // No number found
    return {
      number: undefined,
      title: stripMarkdown(line.replace(/^chapter\s*[:.-]?\s*/i, '').trim())
    };
  };

  // Determine the baseline indent for top-level headings.
  // We look at the first few lines (after skipping the title) to find the
  // indent used by chapter headings — typically 0 or a small consistent value.
  // Lines indented more than the baseline are sub-bullets / notes.
  let baselineIndent = 0;
  for (let i = startIndex; i < lines.length; i++) {
    const cl = lines[i].replace(/^[#*-]+\s*/, '').trim().toLowerCase();
    if (cl.startsWith('introduction') || cl.startsWith('conclusion') ||
        cl.includes('chapter') && /^(?:chapter\s*\d|chapter\s*:)/i.test(cl) ||
        /^\d+[:.)-]\s/.test(lines[i].replace(/^[#*-]+\s*/, '').trim())) {
      baselineIndent = indentLevels[i];
      break;
    }
  }

  // Helper: returns true if a line is indented (sub-bullet / nested note)
  const isSubBullet = (index: number): boolean => {
    return indentLevels[index] > baselineIndent + 1;
  };

  // Helper: returns true if a line looks like a chapter-level heading that starts
  // with "Chapter N" (not just any line that happens to contain the word "chapter").
  const isChapterHeading = (cleanLine: string): boolean => {
    return /^chapter\s*\d/i.test(cleanLine) || /^chapter\s*:/i.test(cleanLine);
  };

  // Process each line (skip the title line if it was consumed as the book title)
  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i];
    const cleanLine = line.replace(/^[#*-]+\s*/, '').trim();
    const lowerLine = cleanLine.toLowerCase();

    // Sub-bullets (indented lines) are always content, never chapter headings
    if (isSubBullet(i)) {
      if (currentChapter) {
        let contentLine = line;
        if (line.startsWith('- ') || line.startsWith('* ')) {
          contentLine = line.substring(2).trim();
        } else if (/^\d+\.\s/.test(line)) {
          contentLine = line.replace(/^\d+\.\s/, '').trim();
        }
        if (contentLine.trim()) {
          currentContent.push(contentLine);
        }
      }
      continue;
    }

    // Check for introduction
    if (lowerLine.startsWith('introduction') || lowerLine.includes('introduction:')) {
      saveCurrentChapter();
      currentChapter = {
        title: 'Introduction',
        content: [],
        type: 'introduction'
      };
      currentContent = [];
    }
    // Check for conclusion
    else if (lowerLine.startsWith('conclusion') || lowerLine.includes('conclusion:')) {
      saveCurrentChapter();
      currentChapter = {
        title: 'Conclusion',
        content: [],
        type: 'conclusion'
      };
      currentContent = [];
    }
    // Check for chapters — only match "Chapter N: ..." headings or numbered lines like "1. Title"
    // NOT lines that merely contain the word "chapter" in a sentence.
    else if (isChapterHeading(cleanLine) || /^\d+[:.)-]\s/.test(cleanLine)) {
      // Extract the title portion to check for metadata keywords that indicate
      // this is a note/description, not an actual chapter heading.
      const metadataKeywords = /^(?:purpose\b|goal\b|objective\b|note\b|description\b|overview\b|summary\b|intent\b|aim\b|estimated\b|total\b|word count\b|appendices\b|structure\b|format\b|length\b)/i;
      const { number, title } = extractChapterNumber(cleanLine);
      const titleClean = stripMarkdown(title);

      if (metadataKeywords.test(titleClean)) {
        // This is a metadata/notes line, not a real chapter — add as content
        if (currentChapter) {
          currentContent.push(cleanLine);
        }
      } else {
        saveCurrentChapter();
        currentChapter = {
          title: titleClean,
          content: [],
          type: 'chapter',
          chapter_number: number
        };
        currentContent = [];
      }
    }
    // Add content to current chapter
    else if (currentChapter) {
      // Clean bullet points and numbering
      let contentLine = line;
      if (line.startsWith('- ') || line.startsWith('* ')) {
        contentLine = line.substring(2).trim();
      } else if (/^\d+\.\s/.test(line)) {
        contentLine = line.replace(/^\d+\.\s/, '').trim();
      }

      if (contentLine.trim()) {
        currentContent.push(contentLine);
      }
    }
  }

  // Save the last chapter
  saveCurrentChapter();

  // Process chapters to ensure correct order and numbering
  const processedChapters: Chapter[] = [];

  // Add introduction if it exists
  const introduction = chapters.find(ch => ch.type === 'introduction');
  if (introduction) {
    processedChapters.push(introduction);
  }

  // Add regular chapters with preserved numbers
  const regularChapters = chapters
    .filter(ch => ch.type === 'chapter')
    .sort((a, b) => {
      // If both chapters have numbers, sort by number
      if (a.chapter_number && b.chapter_number) {
        return a.chapter_number - b.chapter_number;
      }
      // If only one has a number, put the numbered chapter first
      if (a.chapter_number) return -1;
      if (b.chapter_number) return 1;
      // If neither has a number, maintain original order
      return 0;
    });

  // Ensure all chapters have sequential numbers
  let nextNumber = 1;
  regularChapters.forEach(chapter => {
    chapter.chapter_number = nextNumber++;
    processedChapters.push(chapter);
  });

  // Add conclusion if it exists
  const conclusion = chapters.find(ch => ch.type === 'conclusion');
  if (conclusion) {
    processedChapters.push(conclusion);
  }

  // If no chapters were found, throw an error
  if (processedChapters.length === 0) {
    throw new Error('No valid chapters found in the outline. Please check the format and try again.');
  }

  return {
    title: title.trim(),
    chapters: processedChapters
  };
}